// The admin airline monitor's client: the board, the manual checks and the raw answers, all asked with the signed-in
// session. The API decides who is an admin; the browser only shows what it is given.

import { apiFetch } from './supabase'

export type LineState = 'red' | 'yellow' | 'green' | 'quiet'
export type FieldWord = 'same' | 'differs' | 'only_ours' | 'only_live' | 'neither'
export type AccuracyField =
  | 'pieces'
  | 'weight_kg'
  | 'origin'
  | 'destination'
  | 'status'
  | 'last_event'
  | 'eta'

/** One airline as GET /v1/admin/airline-health answers it; only the fields this page shows. */
export interface AdminAirline {
  prefix: string
  iata: string
  name: string
  state: LineState
  calls_24h: number
  failed_24h: number
  failing_streak: number
  last_data_at: string | null
  last_error: string | null
  asked: Array<{ awb: string; calls_24h: number; failed_24h: number; org: string | null }>
  probe: {
    slot: string
    probed_at: string
    verdict: 'ok' | 'failed' | 'no_data' | 'no_sample'
    code: string | null
    failed_days: number
    last_ok_at: string | null
  } | null
  state_by: 'calls' | 'probe'
  refusals: {
    asked_24h: number
    refused_24h: number
    share_24h: number | null
    asked_7d: number
    refused_7d: number
    share_7d: number | null
    level: 'red' | 'yellow' | 'green' | null
  }
  accuracy: {
    slot: string
    probed_at: string
    match: number
    behind: number
    mismatch: number
    no_answer: number
    waybills: Array<{
      awb: string
      org: string | null
      polled_at: string
      outcome: 'ok' | 'failed' | 'dropped'
      code: string | null
      verdict: 'match' | 'behind' | 'mismatch' | null
      behind_h: number | null
      fields: Record<AccuracyField, FieldWord> | null
    }>
  } | null
}

export interface ManualCheck {
  prefix: string
  awb: string | null
  verdict: string
  note: string | null
  checked_by: string
  checked_at: string
}

export interface RawRef {
  path: string
  day: string
  awb: string
  at: string
  source: string
}

/** GET /v1/admin/airlines/:prefix/raw: the newest 500, how many there were, and the days Storage did not give whole. */
export interface RawListing {
  items: RawRef[]
  total: number
  failed_days: string[]
  truncated_days: string[]
}

export type RawState =
  | { status: 'idle' }
  | { status: 'loading' }
  | ({ status: 'ready' } & RawListing)
  | { status: 'failed'; message: string }

/** Admin only: whether the accuracy check runs (AIR_ACCURACY_ENABLED), its newest run and the lines it covered. */
export interface AccuracyRun {
  enabled: boolean
  last_at: string | null
  lines: number
}

/** What the page shows instead of the board when the API refused or failed. */
export type Refusal = { kind: 'signed_out' | 'not_admin' | 'failed'; message: string }

type Answer<T> = { ok: true; data: T } | { ok: false; refusal: Refusal }

async function adminGet<T>(path: string): Promise<Answer<T>> {
  try {
    const res = await apiFetch(path)
    if (res.ok) return { ok: true, data: (res.json as { data: T }).data }
    const message =
      (res.json as { error?: { message?: string } }).error?.message ?? `HTTP ${res.status}`
    if (res.status === 401) return { ok: false, refusal: { kind: 'signed_out', message } }
    if (res.status === 403) return { ok: false, refusal: { kind: 'not_admin', message } }
    return { ok: false, refusal: { kind: 'failed', message } }
  } catch {
    return { ok: false, refusal: { kind: 'failed', message: 'The network is unreachable.' } }
  }
}

export const fetchBoard = () =>
  adminGet<{ generated_at: string; airlines: AdminAirline[]; accuracy_run?: AccuracyRun }>(
    '/v1/admin/airline-health',
  )
export const fetchManualChecks = () => adminGet<ManualCheck[]>('/v1/admin/airline-checks')
export const fetchRawList = (prefix: string) =>
  adminGet<RawListing>(`/v1/admin/airlines/${encodeURIComponent(prefix)}/raw`)
export const fetchRawAnswer = (path: string) =>
  adminGet<unknown>(`/v1/admin/raw?path=${encodeURIComponent(path)}`)

/** Lines customers asked this week, or the accuracy check covered, most-asked first (7 days, then 24 h, then name). */
export function topLines(airlines: AdminAirline[], all: boolean): AdminAirline[] {
  const watched = (a: AdminAirline) => a.refusals.asked_7d > 0 || a.accuracy !== null
  return airlines.filter((a) => all || watched(a)).sort(byRequests)
}

/** Most customer requests over 7 days first, then over 24 hours, then by name. */
function byRequests(a: AdminAirline, b: AdminAirline): number {
  return (
    b.refusals.asked_7d - a.refusals.asked_7d ||
    b.refusals.asked_24h - a.refusals.asked_24h ||
    a.name.localeCompare(b.name)
  )
}

/** A waybill this many hours behind the live line puts the line on the Needs attention list. */
export const BEHIND_ALERT_H = 24

export const FIELD_WORD: Record<AccuracyField, string> = {
  pieces: 'pieces',
  weight_kg: 'weight',
  origin: 'origin',
  destination: 'destination',
  status: 'status',
  last_event: 'last event',
  eta: 'ETA',
}

export interface Attention {
  airline: AdminAirline
  tone: 'red' | 'yellow'
  reasons: string[]
}

/** Lines that are down, refuse customers at the API's level, or fail the accuracy check; most-asked first. */
export function needsAttention(airlines: AdminAirline[]): Attention[] {
  return airlines
    .map((a) => ({ airline: a, reasons: attentionReasons(a), tone: attentionTone(a) }))
    .filter((x) => x.reasons.length > 0)
    .sort((x, y) => byRequests(x.airline, y.airline))
}

function attentionReasons(a: AdminAirline): string[] {
  return [downReason(a), refusalReason(a), mismatchReason(a), behindReason(a)].filter(
    (r): r is string => r !== null,
  )
}

function attentionTone(a: AdminAirline): Attention['tone'] {
  const red = a.state === 'red' || a.refusals.level === 'red' || (a.accuracy?.mismatch ?? 0) > 0
  return red ? 'red' : 'yellow'
}

function downReason(a: AdminAirline): string | null {
  if (a.state !== 'red') return null
  const days = a.probe?.failed_days ?? 0
  return `Down${a.last_error ? `: ${a.last_error}` : ''}${days > 0 ? `, canary failing ${days}d` : ''}`
}

function refusalReason(a: AdminAirline): string | null {
  const r = a.refusals
  if (r.level !== 'red' && r.level !== 'yellow') return null
  return `${percent(r.share_24h)} of requests refused in 24h (${r.refused_24h} of ${r.asked_24h})`
}

function mismatchReason(a: AdminAirline): string | null {
  const all = a.accuracy?.waybills ?? []
  const off = all.filter((w) => w.verdict === 'mismatch')
  if (off.length === 0) return null
  const fields = (Object.keys(FIELD_WORD) as AccuracyField[]).filter((f) =>
    off.some((w) => w.fields?.[f] === 'differs'),
  )
  const which = fields.length > 0 ? `: ${fields.map((f) => FIELD_WORD[f]).join(', ')}` : ''
  return `${off.length} of ${all.length} waybills mismatch${which}`
}

function behindReason(a: AdminAirline): string | null {
  const all = a.accuracy?.waybills ?? []
  const late = all.filter((w) => w.verdict === 'behind' && (w.behind_h ?? 0) >= BEHIND_ALERT_H)
  if (late.length === 0) return null
  const worst = Math.max(...late.map((w) => w.behind_h ?? 0))
  return `${late.length} of ${all.length} waybills ${worst}h behind`
}

export const percent = (share: number | null): string =>
  share === null ? '—' : `${Math.round(share * 100)}%`

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** `27 Oct 2026` in UTC, the board's own clock; months spelled by hand, since ICU writes `Sept` on some machines. */
export const day = (iso: string | null): string => {
  const d = new Date(iso ?? '')
  if (Number.isNaN(d.getTime())) return '—'
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`
}

/** `27 Oct 2026, 08:00 UTC`. */
export const moment = (iso: string | null): string =>
  day(iso) === '—' ? '—' : `${day(iso)}, ${new Date(iso as string).toISOString().slice(11, 16)} UTC`
